import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api';
import type { User } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Plus, Trash2, Camera, Globe, Lock, Link2, Unlink, Crown, KeyRound } from 'lucide-react';
import { ProviderIcon } from '@/components/icons/ProviderIcon';
import { trackEvent } from '@/lib/faro';
import { getRoleLabel, getRoleBadgeColor } from '@/lib/roles';
import { MarkdownEditor } from '@/components/MarkdownEditor';
import { MarkdownRenderer } from '@/components/MarkdownRenderer';

const LINK_PRESETS = [
    { key: 'cashapp', label: 'Cash App', placeholder: '$YourTag' },
    { key: 'venmo', label: 'Venmo', placeholder: '@YourVenmo' },
    { key: 'paypal', label: 'PayPal', placeholder: 'paypal.me/you' },
    { key: 'twitter', label: 'Twitter / X', placeholder: '@handle' },
    { key: 'amazon', label: 'Amazon Wishlist', placeholder: 'https://amazon.com/...' },
    { key: 'lovense', label: 'Lovense Wishlist', placeholder: 'https://lovense.com/...' },
    { key: 'throne', label: 'Throne', placeholder: 'https://throne.com/...' },
    { key: 'onlyfans', label: 'OnlyFans', placeholder: 'https://onlyfans.com/...' },
    { key: 'other', label: 'Other', placeholder: 'https://...' },
];

function SocialLinksDisplay({ links }: { links: Record<string, string> }) {
    const entries = Object.entries(links).filter(([, v]) => v);
    if (entries.length === 0) return null;

    const getLabel = (key: string) =>
        LINK_PRESETS.find((p) => p.key === key)?.label ?? key;

    const isUrl = (val: string) =>
        val.startsWith('http://') || val.startsWith('https://');

    return (
        <div className="space-y-1.5">
            {entries.map(([key, value]) => (
                <div key={key} className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500 min-w-[100px]">{getLabel(key)}</span>
                    {isUrl(value) ? (
                        <a
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-violet-400 hover:underline flex items-center gap-1"
                        >
                            {value.replace(/^https?:\/\//, '').slice(0, 40)}
                            <ExternalLink className="h-3 w-3" />
                        </a>
                    ) : (
                        <span className="text-slate-200">{value}</span>
                    )}
                </div>
            ))}
        </div>
    );
}

const SOCIAL_PROVIDERS: Record<string, string> = {
    google: 'Google',
    discord: 'Discord',
    github: 'GitHub',
    twitter: 'X / Twitter',
    microsoft: 'Microsoft',
};

function LinkedAccounts() {
    const [accounts, setAccounts] = useState<Array<{ provider: string; provider_email: string | null; provider_nickname: string | null; created_at: string }>>([]);
    const [availableProviders, setAvailableProviders] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchAccounts = () => {
        Promise.all([
            api.get<{ accounts: typeof accounts }>('/social-accounts'),
            api.get<{ providers: string[] }>('/auth/providers'),
        ]).then(([accts, provs]) => {
            setAccounts(accts.accounts);
            const linked = new Set(accts.accounts.map((a) => a.provider));
            setAvailableProviders(provs.providers.filter((p) => !linked.has(p)));
            setLoading(false);
        }).catch(() => setLoading(false));
    };

    useEffect(() => { fetchAccounts(); }, []);

    const handleLink = async (provider: string) => {
        localStorage.setItem('social_link_provider', provider);
        const res = await api.get<{ url: string }>(`/social-accounts/${provider}/link`);
        window.location.href = res.url;
    };

    const handleUnlink = async (provider: string) => {
        await api.delete(`/social-accounts/${provider}`);
        fetchAccounts();
    };

    if (loading) return <p className="text-sm text-slate-500">Loading...</p>;

    return (
        <div className="space-y-3">
            {accounts.map((acct) => (
                <div key={acct.provider} className="flex items-center justify-between rounded-md border border-slate-700 p-3">
                    <div className="flex items-center gap-2.5">
                        <ProviderIcon provider={acct.provider} className="h-5 w-5 shrink-0" />
                        <div>
                        <p className="text-sm font-medium">{SOCIAL_PROVIDERS[acct.provider] ?? acct.provider}</p>
                        <p className="text-xs text-slate-400">{acct.provider_email || acct.provider_nickname || 'Connected'}</p>
                        </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleUnlink(acct.provider)} className="text-red-400 hover:text-red-300">
                        <Unlink className="mr-1 h-3.5 w-3.5" />
                        Unlink
                    </Button>
                </div>
            ))}
            {availableProviders.map((provider) => (
                <Button key={provider} variant="outline" size="sm" onClick={() => handleLink(provider)} className="mr-2">
                    <ProviderIcon provider={provider} className="mr-1 h-3.5 w-3.5" />
                    Link {SOCIAL_PROVIDERS[provider] ?? provider}
                </Button>
            ))}
            {accounts.length === 0 && availableProviders.length === 0 && (
                <p className="text-sm italic text-slate-500">No social providers configured.</p>
            )}
        </div>
    );
}

function ChangePassword() {
    const { user } = useAuth();
    const { toast } = useToast();
    const [form, setForm] = useState({ current_password: '', password: '', password_confirmation: '' });
    const [error, setError] = useState('');

    const isOAuthOnly = (user?.linked_accounts?.length ?? 0) > 0;

    const mutation = useMutation({
        mutationFn: (data: typeof form) => api.put('/password', data),
        onSuccess: () => {
            toast('Password updated successfully.', 'success');
            setForm({ current_password: '', password: '', password_confirmation: '' });
            setError('');
        },
        onError: (err) => {
            setError(err instanceof ApiError ? err.message : 'Failed to update password.');
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        mutation.mutate(form);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-3">
            {error && (
                <div className="rounded-md bg-red-900/30 p-2 text-sm text-red-400">{error}</div>
            )}
            {!isOAuthOnly && (
                <div>
                    <label className="mb-1 block text-sm font-medium">Current Password</label>
                    <Input
                        type="password"
                        value={form.current_password}
                        onChange={(e) => setForm((f) => ({ ...f, current_password: e.target.value }))}
                        autoComplete="current-password"
                    />
                </div>
            )}
            <div>
                <label className="mb-1 block text-sm font-medium">New Password</label>
                <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    autoComplete="new-password"
                />
            </div>
            <div>
                <label className="mb-1 block text-sm font-medium">Confirm New Password</label>
                <Input
                    type="password"
                    value={form.password_confirmation}
                    onChange={(e) => setForm((f) => ({ ...f, password_confirmation: e.target.value }))}
                    autoComplete="new-password"
                />
            </div>
            <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Updating...' : 'Update Password'}
            </Button>
        </form>
    );
}

export default function ProfileSettingsTab() {
    const { user, fetchUser } = useAuth();
    const { toast } = useToast();
    const [editing, setEditing] = useState(false);
    const [confirmRelease, setConfirmRelease] = useState(false);
    const [form, setForm] = useState({
        display_name: user?.display_name ?? '',
        bio: user?.bio ?? '',
        role: user?.role ?? 'sub',
        is_profile_public: user?.is_profile_public ?? true,
        show_linked_accounts: user?.show_linked_accounts ?? true,
        show_email: user?.show_email ?? false,
    });
    const [links, setLinks] = useState<Record<string, string>>(user?.social_links ?? {});
    const [newLinkKey, setNewLinkKey] = useState('');
    const [error, setError] = useState('');

    const fileInputRef = useRef<HTMLInputElement>(null);

    const avatarMutation = useMutation({
        mutationFn: (file: File) => {
            const formData = new FormData();
            formData.append('avatar', file);
            return api.post<{ data: User }>('/profile/avatar', formData);
        },
        onSuccess: () => {
            trackEvent('avatar_uploaded');
            toast('Profile photo updated.', 'success');
            fetchUser();
        },
        onError: (err) => {
            toast(err instanceof ApiError ? err.message : 'Avatar upload failed');
        },
    });

    const MAX_AVATAR_BYTES = 64 * 1024 * 1024;
    const handleAvatarFile = (file: File) => {
        if (file.size > MAX_AVATAR_BYTES) {
            toast(`That image is ${(file.size / 1024 / 1024).toFixed(1)}MB — the limit is 64MB.`);
            return;
        }
        avatarMutation.mutate(file);
    };

    const updateMutation = useMutation({
        mutationFn: (data: Partial<User> & { social_links?: Record<string, string> }) =>
            api.put<{ data: User }>('/profile', data),
        onSuccess: () => {
            trackEvent('profile_updated');
            setEditing(false);
            setError('');
            fetchUser();
        },
        onError: (err) => {
            setError(err instanceof ApiError ? err.message : 'Update failed');
        },
    });

    const releaseMutation = useMutation({
        mutationFn: () => api.delete(`/ownership/release/${user!.id}`),
        onSuccess: () => {
            trackEvent('ownership_released');
            toast('You have been released from ownership.', 'success');
            setConfirmRelease(false);
            fetchUser();
        },
        onError: (err: Error) => toast(err.message),
    });

    const handleSave = () => {
        const filteredLinks = Object.fromEntries(
            Object.entries(links).filter(([, v]) => v.trim())
        );
        const { role, ...rest } = form;
        const payload: Record<string, unknown> = {
            ...rest,
            social_links: Object.keys(filteredLinks).length > 0 ? filteredLinks : null,
        };
        if (role !== user?.role) {
            payload.role = role;
        }
        updateMutation.mutate(payload as Partial<User> & { social_links: Record<string, string> | null });
    };

    const startEditing = () => {
        setForm({
            display_name: user?.display_name ?? '',
            bio: user?.bio ?? '',
            role: user?.role ?? 'sub',
            is_profile_public: user?.is_profile_public ?? true,
            show_linked_accounts: user?.show_linked_accounts ?? true,
            show_email: user?.show_email ?? false,
        });
        setLinks(user?.social_links ?? {});
        setEditing(true);
    };

    const addLink = () => {
        if (newLinkKey && !(newLinkKey in links)) {
            setLinks((l) => ({ ...l, [newLinkKey]: '' }));
            setNewLinkKey('');
        }
    };

    const removeLink = (key: string) => {
        setLinks((l) => {
            const next = { ...l };
            delete next[key];
            return next;
        });
    };

    if (!user) return null;

    const socialLinks = user.social_links ?? {};
    const hasLinks = Object.keys(socialLinks).length > 0;
    const availablePresets = LINK_PRESETS.filter((p) => !(p.key in links));

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-end">
                {!editing && (
                    <Button variant="outline" size="sm" onClick={startEditing}>
                        Edit
                    </Button>
                )}
            </div>

            <Card>
                <CardHeader>
                    <div className="flex items-center gap-3">
                        <div className="relative group">
                            {user.avatar_path ? (
                                <img
                                    src={user.avatar_path}
                                    alt={user.display_name}
                                    className="h-12 w-12 rounded-full object-cover"
                                />
                            ) : (
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-violet-900/50 text-lg font-bold text-violet-300">
                                    {user.display_name?.[0]?.toUpperCase() ?? '?'}
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            >
                                <Camera className="h-5 w-5 text-white" />
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleAvatarFile(file);
                                    e.target.value = '';
                                }}
                            />
                        </div>
                        <div className="min-w-0">
                            <CardTitle className="text-lg">{user.display_name}</CardTitle>
                            {user.email && (
                                <p className="text-xs text-slate-400 mt-0.5">{user.email}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                                <Badge className={`${getRoleBadgeColor(user.role)}`}>{getRoleLabel(user.role)}</Badge>
                                <Badge variant="outline" className={user.is_profile_public ? 'border-green-700 text-green-400' : 'border-slate-700 text-slate-400'}>
                                    {user.is_profile_public ? <><Globe className="mr-1 h-3 w-3" />Public</> : <><Lock className="mr-1 h-3 w-3" />Private</>}
                                </Badge>
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {editing ? (
                        <div className="space-y-4">
                            {error && (
                                <div className="rounded-md bg-red-900/30 p-2 text-sm text-red-400">
                                    {error}
                                </div>
                            )}
                            <div>
                                <label className="mb-1 block text-sm font-medium">
                                    Display Name
                                </label>
                                <Input
                                    value={form.display_name}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, display_name: e.target.value }))
                                    }
                                />
                            </div>
                            {!user.role || user.role === 'dom' || user.role === 'sub' || user.role === 'switch' ? (
                                <div>
                                    <label className="mb-1 block text-sm font-medium">Role</label>
                                    {user.owner ? (
                                        <p className="text-xs text-slate-500">You cannot change your role while you have an owner.</p>
                                    ) : (
                                        <div className="flex gap-1 rounded-lg bg-slate-800 p-1">
                                            {([['dom', 'Dominant'], ['sub', 'Submissive'], ['switch', 'Switch']] as const).map(([value, label]) => (
                                                <button
                                                    key={value}
                                                    type="button"
                                                    onClick={() => setForm((f) => ({ ...f, role: value }))}
                                                    className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                                                        form.role === value
                                                            ? value === 'dom' ? 'bg-red-900/50 text-red-300'
                                                            : value === 'sub' ? 'bg-violet-900/50 text-violet-300'
                                                            : 'bg-amber-900/50 text-amber-300'
                                                            : 'text-slate-500 hover:text-slate-300'
                                                    }`}
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : null}
                            <div>
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={form.is_profile_public}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, is_profile_public: e.target.checked }))
                                        }
                                        className="rounded border-slate-600"
                                    />
                                    <span>Public profile</span>
                                    <span className="text-xs text-slate-500">
                                        (visible to anyone)
                                    </span>
                                </label>
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={form.show_linked_accounts}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, show_linked_accounts: e.target.checked }))
                                        }
                                        className="rounded border-slate-600"
                                    />
                                    <span>Show linked accounts on profile</span>
                                    <span className="text-xs text-slate-500">
                                        (Discord, GitHub, etc.)
                                    </span>
                                </label>
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={form.show_email}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, show_email: e.target.checked }))
                                        }
                                        className="rounded border-slate-600"
                                    />
                                    <span>Show email on profile</span>
                                    <span className="text-xs text-slate-500">
                                        (visible to other members)
                                    </span>
                                </label>
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-medium">Bio</label>
                                <p className="mb-2 text-xs text-slate-500">Supports Markdown: **bold**, _italic_, links, lists, headings, and more.</p>
                                <MarkdownEditor
                                    value={form.bio}
                                    onChange={(bio) => setForm((f) => ({ ...f, bio }))}
                                    placeholder="Tell others about yourself..."
                                    rows={6}
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    onClick={handleSave}
                                    disabled={updateMutation.isPending}
                                >
                                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => setEditing(false)}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            {user.bio ? (
                                <MarkdownRenderer content={user.bio} />
                            ) : (
                                <p className="text-sm italic text-slate-500">No bio yet.</p>
                            )}
                            {user.average_rating && (
                                <p className="mt-2 text-sm text-slate-400">
                                    Rating: {user.average_rating} ({user.review_count} reviews)
                                </p>
                            )}
                            {user.owner && (
                                <div className="mt-3 space-y-2">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Crown className="h-4 w-4 text-amber-400" />
                                        <span className="text-slate-400">Owned by</span>
                                        <Link to={`/profiles/${user.owner.id}`} className="text-violet-400 hover:underline">
                                            {user.owner.display_name}
                                        </Link>
                                    </div>
                                    {!confirmRelease ? (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                            onClick={() => setConfirmRelease(true)}
                                        >
                                            <Unlink className="h-3.5 w-3.5 mr-1" />
                                            Leave Owner
                                        </Button>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-slate-400">Are you sure?</span>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => releaseMutation.mutate()}
                                                disabled={releaseMutation.isPending}
                                            >
                                                {releaseMutation.isPending ? 'Leaving...' : 'Yes, Leave'}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setConfirmRelease(false)}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Links & Wishlists */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Links & Wishlists</CardTitle>
                </CardHeader>
                <CardContent>
                    {editing ? (
                        <div className="space-y-3">
                            {Object.entries(links).map(([key, value]) => {
                                const preset = LINK_PRESETS.find((p) => p.key === key);
                                return (
                                    <div key={key} className="flex items-center gap-2">
                                        <span className="min-w-[100px] text-sm text-slate-400">
                                            {preset?.label ?? key}
                                        </span>
                                        <Input
                                            value={value}
                                            placeholder={preset?.placeholder ?? 'Value'}
                                            onChange={(e) =>
                                                setLinks((l) => ({ ...l, [key]: e.target.value }))
                                            }
                                            className="flex-1"
                                        />
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                                            onClick={() => removeLink(key)}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                );
                            })}

                            {availablePresets.length > 0 && (
                                <div className="flex items-center gap-2 border-t border-slate-800 pt-3">
                                    <select
                                        value={newLinkKey}
                                        onChange={(e) => setNewLinkKey(e.target.value)}
                                        className="rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100"
                                    >
                                        <option value="">Add a link...</option>
                                        {availablePresets.map((p) => (
                                            <option key={p.key} value={p.key}>{p.label}</option>
                                        ))}
                                    </select>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={addLink}
                                        disabled={!newLinkKey}
                                    >
                                        <Plus className="mr-1 h-3.5 w-3.5" />
                                        Add
                                    </Button>
                                </div>
                            )}
                        </div>
                    ) : hasLinks ? (
                        <SocialLinksDisplay links={socialLinks} />
                    ) : (
                        <p className="text-sm italic text-slate-500">No links added yet.</p>
                    )}
                </CardContent>
            </Card>

            {/* Linked Accounts */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Linked Accounts</CardTitle>
                </CardHeader>
                <CardContent>
                    <LinkedAccounts />
                </CardContent>
            </Card>

            {/* Change Password */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <KeyRound className="h-4 w-4" />
                        Change Password
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <ChangePassword />
                </CardContent>
            </Card>
        </div>
    );
}
